import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  ArrowLeft, 
  FileText, 
  Download, 
  Send, 
  XCircle,
  Clock,
  CheckCircle,
  AlertTriangle,
  QrCode,
  Link as LinkIcon,
  Copy
} from 'lucide-react';
import api from '../../../lib/api';
import { ContractViewer } from '../../../components/contracts/ContractViewer';
import { SignatureStatus } from '../../../components/contracts/SignatureStatus';
import { QRCodeVerification } from '../../../components/contracts/QRCodeVerification';
import { useState } from 'react';

type ContractStatus = 'DRAFT' | 'PENDING_SIGNATURES' | 'PARTIALLY_SIGNED' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';

interface ContractSignature {
  id: string;
  signerRole: string;
  signerName: string;
  signerEmail: string;
  status: 'PENDING' | 'SIGNED' | 'DECLINED' | 'EXPIRED';
  signedAt?: string;
  ipAddress?: string;
  geolocation?: {
    latitude: number;
    longitude: number;
  };
  order: number;
}

interface DigitalContract {
  id: string;
  tenantId: string;
  templateId: string;
  templateVersion: number;
  content: string;
  contentHash: string;
  variables: Record<string, string>;
  assetId?: string;
  status: ContractStatus;
  verificationCode: string;
  qrCodeUrl?: string;
  createdAt: string;
  sentAt?: string;
  completedAt?: string;
  expiresAt?: string;
  template?: {
    name: string;
    category: string;
  };
  asset?: {
    name: string;
  };
  signatures: ContractSignature[];
}

const STATUS_CONFIG: Record<ContractStatus, { icon: typeof CheckCircle; color: string; bgColor: string; label: string }> = {
  DRAFT: { icon: FileText, color: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Rascunho' },
  PENDING_SIGNATURES: { icon: Clock, color: 'text-yellow-600', bgColor: 'bg-yellow-100', label: 'Aguardando Assinaturas' },
  PARTIALLY_SIGNED: { icon: AlertTriangle, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Parcialmente Assinado' },
  COMPLETED: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Concluído' },
  CANCELLED: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Cancelado' },
  EXPIRED: { icon: AlertTriangle, color: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Expirado' },
};

export function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showQRCode, setShowQRCode] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data: contract, isLoading } = useQuery({
    queryKey: ['contract', id],
    queryFn: async () => {
      const response = await api.get<DigitalContract>(`/contracts/${id}`);
      return response.data;
    },
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/contracts/${id}/send`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/contracts/${id}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
    },
  });

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/contracts/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato-${contract?.verificationCode}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/verify/${contract?.verificationCode}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Contrato não encontrado</p>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[contract.status];
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/contracts')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  Contrato #{contract.verificationCode.slice(0, 8)}
                </h1>
                <p className="text-sm text-gray-500">
                  {contract.template?.name} • v{contract.templateVersion}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowQRCode(!showQRCode)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                  showQRCode ? 'bg-blue-50 border-blue-200 text-blue-700' : 'hover:bg-gray-50'
                }`}
              >
                <QrCode className="w-4 h-4" />
                QR Code
              </button>
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                PDF
              </button>
              {contract.status === 'DRAFT' && (
                <button
                  onClick={() => sendMutation.mutate()}
                  disabled={sendMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  Enviar
                </button>
              )}
              {(contract.status === 'DRAFT' || contract.status === 'PENDING_SIGNATURES') && (
                <button
                  onClick={() => {
                    if (confirm('Tem certeza que deseja cancelar este contrato?')) {
                      cancelMutation.mutate();
                    }
                  }}
                  disabled={cancelMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Cancelar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Main content */}
          <div className="col-span-2 space-y-6">
            {/* Status card */}
            <div className={`p-4 rounded-lg ${statusConfig.bgColor}`}>
              <div className="flex items-center gap-3">
                <StatusIcon className={`w-6 h-6 ${statusConfig.color}`} />
                <div>
                  <p className={`font-medium ${statusConfig.color}`}>{statusConfig.label}</p>
                  {contract.completedAt && (
                    <p className="text-sm opacity-75">
                      Concluído em {format(new Date(contract.completedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Contract viewer */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b">
                <h2 className="font-semibold">Conteúdo do Contrato</h2>
              </div>
              <ContractViewer
                content={contract.content}
                variables={contract.variables}
                status={contract.status}
                onDownload={handleDownloadPDF}
                className="h-[600px]"
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* QR Code modal */}
            {showQRCode && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold mb-4">Verificação Pública</h3>
                <QRCodeVerification
                  verificationCode={contract.verificationCode}
                  size="md"
                />
              </div>
            )}

            {/* Info card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Informações</h3>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">ID do Contrato</dt>
                  <dd className="font-mono text-gray-900">{contract.id.slice(0, 16)}...</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Código de Verificação</dt>
                  <dd className="font-mono text-gray-900">{contract.verificationCode}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Hash do Documento</dt>
                  <dd className="font-mono text-xs text-gray-900 break-all">
                    {contract.contentHash.slice(0, 32)}...
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Criado em</dt>
                  <dd className="text-gray-900">
                    {format(new Date(contract.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </dd>
                </div>
                {contract.sentAt && (
                  <div>
                    <dt className="text-gray-500">Enviado em</dt>
                    <dd className="text-gray-900">
                      {format(new Date(contract.sentAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </dd>
                  </div>
                )}
                {contract.expiresAt && (
                  <div>
                    <dt className="text-gray-500">Expira em</dt>
                    <dd className="text-gray-900">
                      {format(new Date(contract.expiresAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </dd>
                  </div>
                )}
                {contract.asset && (
                  <div>
                    <dt className="text-gray-500">Ativo Vinculado</dt>
                    <dd className="text-gray-900">{contract.asset.name}</dd>
                  </div>
                )}
              </dl>

              {/* Verification link */}
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500 mb-2">Link de Verificação</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/verify/${contract.verificationCode}`}
                    readOnly
                    className="flex-1 text-xs px-2 py-1 bg-gray-50 border rounded"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Copiar link"
                  >
                    {copied ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  <a
                    href={`/verify/${contract.verificationCode}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Abrir link"
                  >
                    <LinkIcon className="w-4 h-4 text-gray-400" />
                  </a>
                </div>
              </div>
            </div>

            {/* Signatures */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Assinaturas</h3>
              <SignatureStatus
                signatures={contract.signatures}
                showTimeline
                showDetails
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
